import type { Home, ResidentItem } from '@lmc/ts-common'
import { Avatar, Button, EditTooltip, Heading } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import type { ResidentFetchItemResponse } from '@lmc/validations'
import {
  hasMeaningfulStoredHtml,
  storedHtmlToPlainText
} from 'common/utils/html'
import Description from 'components/generic/LogTimelineRow/Description'
import { ViewStoredHtml } from 'components/generic/ViewStoredHtml'
import useFeatureFlags from 'hooks/useFeatureFlags'
import useResidentNeeds from 'hooks/useResidentNeeds'
import { NAMESPACE } from 'locale'
import { generalConstants } from 'locale/general'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useAlertsList } from 'screens/Residents/screens/ResidentAlertsScreen/hooks/useAlertsList'
import styled from 'styled-components'
import { getResidentInfoDetail } from '../utils/utils'
import { GeneralAlertsSection } from './GeneralAlertsSection'
import { GeneralDNACPRSection } from './GeneralDNACPRSection'
import { GeneralNeedsSection } from './GeneralNeedsSection'
import { GeneralStatusModal } from './GeneralStatusModal'
import { GeneralZoneSection } from './GeneralZoneSection'
import { ProfileBadges } from './ProfileBadges'

type GeneralInformationProps = {
  resident: ResidentItem
  home: Home
  testId?: string
}

export const GeneralInformation = ({
  resident,
  home,
  testId
}: GeneralInformationProps) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)
  const { id } = useParams()

  const flags = useFeatureFlags({
    flagNames: ['mods_alerts', 'mods_demographics', 'profile_rich_text']
  })?.data

  const hasPro = home?.features?.pro?.enabled
  const hasSkillsAndNeeds = home?.features?.skillsAndNeeds?.enabled

  const alertsEnabled = flags?.mods_alerts?.enabled ?? false
  const isModsDemographicsEnabled = flags?.mods_demographics?.enabled
  const richTextEnabled = flags?.profile_rich_text?.enabled ?? false

  const { alerts: activeAlerts } = useAlertsList({
    residentId: id,
    status: ['active'],
    enabled: alertsEnabled && !!id
  })

  const hasActiveAlerts = activeAlerts.length > 0

  const { needs: residentNeedsData } = useResidentNeeds({
    id: hasPro && hasSkillsAndNeeds ? (id ?? '') : ''
  })

  const hasNeeds = residentNeedsData.length > 0

  const residentNeeds = residentNeedsData
    .map((n) => n.need?.name)
    .filter(Boolean)

  const hasSummarySection = richTextEnabled
    ? hasMeaningfulStoredHtml(resident?.summary)
    : !!resident?.summary

  return (
    <MainWrapper
      data-testid={testId ? `${testId}-general-info` : 'resident-general-info'}
    >
      <GeneralWrapper
        data-testid={
          testId ? `${testId}-general-wrapper` : 'resident-general-wrapper'
        }
      >
        <LeftWrapper
          data-testid={
            testId ? `${testId}-left-wrapper` : 'resident-left-wrapper'
          }
        >
          <AvatarWrapper
            data-testid={
              testId ? `${testId}-avatar-wrapper` : 'resident-avatar-wrapper'
            }
          >
            <Avatar
              url={resident?.picture as unknown as string}
              size={140}
              data-testid={testId ? `${testId}-avatar` : 'resident-avatar'}
            />
          </AvatarWrapper>
          <InfoWrapper
            data-testid={
              testId ? `${testId}-info-wrapper` : 'resident-info-wrapper'
            }
          >
            <Heading
              level={2}
              data-testid={testId ? `${testId}-name` : 'resident-name'}
            >
              {resident?.preferredName || resident?.name?.first}
            </Heading>
            <StyledText
              data-testid={testId ? `${testId}-details` : 'resident-details'}
            >
              {getResidentInfoDetail(
                resident as ResidentFetchItemResponse,
                isModsDemographicsEnabled
              )}
            </StyledText>
            <ProfileBadges resident={resident} testId={testId} />
          </InfoWrapper>
        </LeftWrapper>
        <GeneralStatusModal
          resident={resident as ResidentFetchItemResponse}
          testId={testId}
        />
      </GeneralWrapper>
      {hasPro && <GeneralDNACPRSection resident={resident} testId={testId} />}
      <GeneralZoneSection resident={resident} hasPro={hasPro} testId={testId} />
      {hasPro && hasNeeds && (
        <GeneralNeedsSection
          needsText={residentNeeds?.filter(Boolean)?.join(', ')}
          testId={testId}
        />
      )}
      {alertsEnabled && hasActiveAlerts && (
        <GeneralAlertsSection
          alertsText={[...new Set(activeAlerts.map((a) => a.label))].join(', ')}
          testId={testId}
        />
      )}
      {hasSummarySection && (
        <CoreSectionWrapper
          data-testid={
            testId ? `${testId}-summary-section` : 'resident-summary-section'
          }
        >
          <CoreSection
            data-testid={
              testId ? `${testId}-summary-content` : 'resident-summary-content'
            }
          >
            <StyledCoreLabel level={2}>
              <EditTooltip title={t(supConstants.PERSONAL_NOTES_TOOLTIP)}>
                {t(supConstants.PERSONAL_NOTES)}
              </EditTooltip>
            </StyledCoreLabel>
            {richTextEnabled ? (
              <RichTextSummary
                html={resident?.summary ?? ''}
                testId={
                  testId
                    ? `${testId}-summary-description`
                    : 'resident-summary-description'
                }
              />
            ) : (
              <Description
                description={resident?.summary}
                data-testid={
                  testId
                    ? `${testId}-summary-description`
                    : 'resident-summary-description'
                }
              />
            )}
          </CoreSection>
        </CoreSectionWrapper>
      )}
    </MainWrapper>
  )
}

const GeneralWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
  }
`

const LeftWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
`

const StyledText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    text-align: center;
    margin-top: 5px;
  }
`

const StyledCoreLabel = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`

const InfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
`

const CoreSectionWrapper = styled.div`
  border-top: 1px solid;
  border-color: ${(props) => props.theme.color.neutral.color7};
  margin-top: 16px;
  padding-top: 16px;
  display: flex;
  justify-content: space-between;
`

const CoreSection = styled.div`
  display: flex;
  flex-direction: column;
`

const AvatarWrapper = styled.div`
  width: 140px;
  height: 140px;
`

const MainWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${(props) => props.theme.color.neutral.color11};
  padding: 24px;
  border-radius: ${(props) => props.theme.properties.borderRadius.large};
`

const PLAIN_TEXT_TRUNCATE_AT = 500

function RichTextSummary({ html, testId }: { html: string; testId?: string }) {
  const [isCollapsed, setIsCollapsed] = useState(true)
  const { t } = useTranslation(NAMESPACE.GENERAL)
  const contentId = useId()
  const plain = storedHtmlToPlainText(html)
  const isLong = plain.length > PLAIN_TEXT_TRUNCATE_AT

  return (
    <div data-testid={testId}>
      <RichTextClip id={contentId} $collapsed={isLong && isCollapsed}>
        <ViewStoredHtml html={html} />
      </RichTextClip>
      {isLong && isCollapsed && (
        <ShowMoreButton
          variant="text"
          aria-expanded={false}
          aria-controls={contentId}
          onClick={() => setIsCollapsed(false)}
        >
          {t(generalConstants.SHOW_MORE)}
        </ShowMoreButton>
      )}
      {isLong && !isCollapsed && (
        <ShowMoreButton
          variant="text"
          aria-expanded={true}
          aria-controls={contentId}
          onClick={() => setIsCollapsed(true)}
        >
          {t(generalConstants.SHOW_LESS)}
        </ShowMoreButton>
      )}
    </div>
  )
}

const RichTextClip = styled.div<{ $collapsed: boolean }>`
  overflow: hidden;
  max-height: ${({ $collapsed }) => ($collapsed ? '7.5em' : 'none')};
`

const ShowMoreButton = styled(Button)`
  display: inline;
  margin-left: 0;
  margin-top: 4px;
` as typeof Button
